"use client";

import { useSession } from "next-auth/react";
import { useRouter, usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { AddEmailPrompt } from "@/components/auth/AddEmailPrompt";
import { AddWalletPrompt } from "@/components/auth/AddWalletPrompt";

function EmailVerificationBanner() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResend = async () => {
    setSending(true);
    try {
      const response = await fetch("/api/auth/resend-verification", { method: "POST" });
      if (response.ok) {
        setSent(true);
      }
    } catch {
      // Silently fail
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-md px-4 py-3 mb-6 flex items-center justify-between">
      <p className="text-sm text-yellow-800">
        Please verify your email address — it&apos;s required to be entered into
        prize drawings. Check your inbox for a verification link.
      </p>
      {sent ? (
        <span className="text-sm text-green-700 font-medium">Sent!</span>
      ) : (
        <button
          onClick={handleResend}
          disabled={sending}
          className="text-sm font-medium text-yellow-800 underline hover:text-yellow-900 disabled:opacity-50"
        >
          {sending ? "Sending..." : "Resend email"}
        </button>
      )}
    </div>
  );
}

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: session, status } = useSession();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (status === "loading") return; // Still loading
    if (!session) {
      // Store the intended destination so the user returns here after auth
      sessionStorage.setItem("returnUrl", pathname);
      router.push("/auth/signin");
      return;
    }
  }, [session, status, router, pathname]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      {/* Account-completion nudges, in priority order:
          - No email at all (wallet-only login) → add+verify one via Privy.
          - Has an email but unverified → verify it.
          - Verified email but no wallet → connect one, else claims only reserve. */}
      {!session.user.email ? (
        <AddEmailPrompt />
      ) : !session.user.emailVerified ? (
        <EmailVerificationBanner />
      ) : !session.user.hasWallet ? (
        <AddWalletPrompt />
      ) : null}
      {children}
    </div>
  );
}
